<script setup lang="ts">
import { ref } from 'vue'
import { useCluesStore } from '@/stores/clues'
import { useToastStore } from '@/stores/toast'
import FosDialog from '@/components/ui/FosDialog.vue'
import FosButton from '@/components/ui/FosButton.vue'
import FosTextarea from '@/components/ui/FosTextarea.vue'
import CoordinateInput from '@/components/ui/CoordinateInput.vue'

const props = defineProps<{
  visible: boolean
  incidentId: string
}>()

const emit = defineEmits<{
  'update:visible': [value: boolean]
}>()

const cluesStore = useCluesStore()
const toast = useToastStore()

const description = ref('')
const parsedCoords = ref<{ lat: number | null; lng: number | null }>({ lat: null, lng: null })
const foundBy = ref('')
const saving = ref(false)

function close() {
  emit('update:visible', false)
}

function reset() {
  description.value = ''
  parsedCoords.value = { lat: null, lng: null }
  foundBy.value = ''
}

function handleOpenChange(open: boolean) {
  if (!open) {
    reset()
    close()
  }
}

async function submit() {
  if (!description.value.trim()) return
  saving.value = true
  try {
    await cluesStore.addClue(props.incidentId, {
      description: description.value.trim(),
      lat: parsedCoords.value.lat,
      lng: parsedCoords.value.lng,
      found_by: foundBy.value.trim(),
      title: null,
      location: null,
      instructions: null,
      caltopo_clue_id: null,
      caltopo_assignment_id: null,
      last_synced_at: null,
      caltopo_deleted: false,
    })
    toast.add('Clue logged', 'success')
    reset()
    close()
  } catch (e) {
    toast.add('Failed to log clue', 'error')
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <FosDialog
    :open="visible"
    title="Log Clue"
    @update:open="handleOpenChange"
  >
    <div class="flex flex-col gap-4 py-2">
      <div class="flex flex-col gap-1">
        <label class="text-[10px] text-outline font-label uppercase tracking-widest">
          Description <span class="text-error">*</span>
        </label>
        <FosTextarea
          v-model="description"
          :rows="3"
          placeholder="Describe the clue..."
        />
      </div>

      <CoordinateInput
        :lat="parsedCoords.lat"
        :lng="parsedCoords.lng"
        label="Coordinates"
        @update:coords="parsedCoords = $event"
      />

      <div class="flex flex-col gap-1">
        <label class="text-[10px] text-outline font-label uppercase tracking-widest">Found By</label>
        <FosInput
          v-model="foundBy"
          placeholder="Team ID or person name"
        />
      </div>
    </div>

    <template #footer>
      <FosButton variant="tertiary" @click="close">Cancel</FosButton>
      <FosButton
        variant="primary"
        :disabled="saving || !description.trim()"
        @click="submit"
      >
        <span v-if="saving" class="w-4 h-4 border-2 border-outline/30 border-t-outline animate-spin inline-block" />
        Log Clue
      </FosButton>
    </template>
  </FosDialog>
</template>
